import React, { createContext, useState, useEffect, useRef, useMemo } from 'react';
import { Option, CardWithBrand, SortOption } from './types';
import { ProductType } from 'common/api/e-comm/models/Product';
import { Card } from 'common/api/e-comm/models/Card';
import { noop } from 'common/utils';
import { allCards, sortOptions, sortCards, getCardTypeOptions } from './util';
import { useRootSelector } from 'common/features/featuresReducer';
import { selectUserCards } from 'common/features/store/duck/wallet/duck';
import { useHistory } from 'react-router';

/*
 * The context provides the following:
 * - State for holding the brand filter and cardType filter
 * - functions to update the state for both filter types
 * - helper function that allows logic to be shared between mobile/desktop
 *   for the CardType filter logic.
 * - The result string of the number of viewable cards after filters have been applied
 */

export interface WalletSortAndFilterContext {
    // filter functions
    filterByCardType: (_: string[]) => void;
    filterByBrand: (_: Option[]) => void;

    // state for card-type filters
    activeCardTypeFilter: Set<Option>;
    setActiveCardTypeFilter: React.Dispatch<React.SetStateAction<Set<Option>>>;

    // state for brand-type filters
    selectedBrands: Option[];
    setSelectedBrands: React.Dispatch<React.SetStateAction<Option[]>>;

    // state for controlling viewable cards
    cardsWithBrand: CardWithBrand[];
    setViewableCards: (_: Card[]) => void;

    // function that encapsulates the logic for resolving the cardType filters
    // logic
    resolveCardTypeActiveFilters: (__: Option) => Set<Option>;

    result: string;
    shouldResetFilters: boolean;

    // sort
    sortedBy: SortOption;
    setSortedBy: (_: SortOption) => void;
}

const initData: WalletSortAndFilterContext = {
    filterByCardType: noop,
    filterByBrand: noop,
    activeCardTypeFilter: new Set<Option>([allCards]),
    selectedBrands: [],
    setActiveCardTypeFilter: noop,
    setSelectedBrands: noop,
    cardsWithBrand: [],
    setViewableCards: noop,
    resolveCardTypeActiveFilters: () => new Set<Option>([]),
    result: '00',
    shouldResetFilters: true,
    sortedBy: sortOptions.addedRecentlyOption,
    setSortedBy: () => undefined,
};

const resolveCardTypeActiveFilters = (cardType: Option) => {
    const updatedFilters: Set<Option> = new Set([]);

    updatedFilters.add(cardType);

    return updatedFilters;
};

export type WalletSortAndFilterContextType = Omit<
    WalletSortAndFilterContext,
    | 'setViewableCards'
    | 'cardsWithBrand'
    | 'filterByCardType'
    | 'filterByBrand'
    | 'shouldResetFilters'
>;

export const SESSION_STORAGE_KEY = 'WalletFilters';

export const WalletSortAndFilterContext = createContext<WalletSortAndFilterContextType>(initData);

const getStateFromSessionStorage = (key: string) => {
    const walletFiltersJson = sessionStorage.getItem(`${SESSION_STORAGE_KEY}_${key}`);
    if (walletFiltersJson) {
        const rawObj = JSON.parse(walletFiltersJson) as WalletSortAndFilterContextType;
        // Filters are matched by object identity (Set#has), so a persisted value must be
        // mapped back onto the canonical option. Anything the current brand does not
        // offer falls back to "All Cards".
        const cardTypeOptions = getCardTypeOptions();
        rawObj.activeCardTypeFilter = new Set(
            Array.from(rawObj.activeCardTypeFilter).map(
                (ctf) => cardTypeOptions.find((option) => option.value === ctf.value) ?? allCards
            )
        );
        return rawObj;
    }
};

const setStateIntoSessionStorage = (key: string, state: {}) => {
    sessionStorage.setItem(
        `${SESSION_STORAGE_KEY}_${key}`,
        JSON.stringify(state, (_key, value) =>
            value instanceof Set ? Array.from(value.values()) : value
        )
    );
};

type WalletSortAndFilterViewProps = Pick<
    WalletSortAndFilterContext,
    'result' | 'cardsWithBrand' | 'setViewableCards' | 'shouldResetFilters'
> & {
    shouldPersistFilter: boolean;
};

export const WalletSortAndFilterView = ({
    result,
    cardsWithBrand,
    setViewableCards,
    shouldPersistFilter,
    children,
}: React.PropsWithChildren<WalletSortAndFilterViewProps>) => {
    const history = useHistory();
    const [initialLocation] = useState(history.location.pathname);

    const persistedFilter = useMemo(
        () => (shouldPersistFilter ? getStateFromSessionStorage(initialLocation) : undefined),
        [shouldPersistFilter, initialLocation]
    );

    const [resultString, setResult] = useState<string>('00');
    const [activeCardTypeFilter, setActiveCardTypeFilter] = useState<Set<Option>>(
        persistedFilter?.activeCardTypeFilter || new Set<Option>([allCards])
    );
    const [selectedBrands, setSelectedBrands] = useState<Option[]>(
        persistedFilter?.selectedBrands || []
    );
    const [sortedBy, setSortedBy] = useState(
        persistedFilter?.sortedBy || sortOptions.addedRecentlyOption
    );
    const cards = useRef<CardWithBrand[]>([]);
    const c = useRootSelector(selectUserCards);

    useEffect(() => {
        cards.current = cardsWithBrand;
    }, [cardsWithBrand]);

    // persists the filter state into the sessionStorage
    // so when the user returns the state is preserved.
    useEffect(() => {
        if (!shouldPersistFilter) {
            return;
        }

        setStateIntoSessionStorage(initialLocation, {
            activeCardTypeFilter,
            selectedBrands,
            sortedBy,
        } as WalletSortAndFilterContextType);
    }, [activeCardTypeFilter, selectedBrands, sortedBy, shouldPersistFilter, initialLocation]);

    useEffect(() => {
        setResult(parseInt(result) < 10 ? `0${result}` : result.toString());
    }, [result]);

    useEffect(() => {
        const filterByCardType = (card: CardWithBrand) => {
            return Array.from(activeCardTypeFilter.values()).some(
                (filter: Option) =>
                    filter.value === 'all' || card.filterTypes.has(filter.value as ProductType)
            );
        };

        const filterByBrand = (card: CardWithBrand) => {
            return (
                selectedBrands.some(
                    (filter: Option) => filter.value === card.brand?.id.toString()
                ) || selectedBrands.length === 0
            );
        };

        const sort = (cardA: CardWithBrand, cardB: CardWithBrand) =>
            sortCards(cardA, cardB, sortedBy);

        setViewableCards(cards.current.filter(filterByCardType).filter(filterByBrand).sort(sort));
    }, [activeCardTypeFilter, selectedBrands, setViewableCards, sortedBy, c, cards.current.length]);

    const context = {
        sortedBy,
        setSortedBy,
        activeCardTypeFilter,
        setActiveCardTypeFilter,
        selectedBrands,
        setSelectedBrands,
        result: resultString,
        resolveCardTypeActiveFilters,
    };
    return (
        <WalletSortAndFilterContext.Provider value={context}>
            <div>{children} </div>
        </WalletSortAndFilterContext.Provider>
    );
};
